import { Box, Divider, Table, TableBody, TableContainer, TableHead, Typography } from "@mui/material";
import TableRowComponent, { DataHeaderType } from "components/table/TableRowComponent";
import { HEADER_EST_PDF } from "constants/data";
import { QuoteTireDetail } from "features/quotation/types";
import { formatCurrency, isPriceLabel } from "utils";
import { useMemo } from "react";
import Barcode from "components/Barcode";

/**
 * TableEstResultPdf component renders a table with a label and optional sx prop for styling.
 *
 * @param {{ quotation: QuoteTireDetail }} root0 - The props object containing the quotation.
 */
export default function TableEstResultPdf({ quotation }: { quotation: QuoteTireDetail }) {
  const cashback = useMemo(() => {
    if (!quotation.cb_tire_price && !quotation.cb_wheel_price) return "-";
    return formatCurrency((quotation.cb_tire_price ?? 0) + (quotation.cb_wheel_price ?? 0));
  }, [quotation.cb_tire_price, quotation.cb_wheel_price]);

  const discountAmount = useMemo(() => 
     (quotation.confirm_total_price ?? 0) - (
      (quotation.discounted_sale_price ?? 0) +
      (quotation.wage_cost_price ?? 0) +
      (quotation.puncture_protection_price ?? 0)
    )
  , [quotation.confirm_total_price, quotation.discounted_sale_price, quotation.wage_cost_price, quotation.puncture_protection_price]);

  const rowData: DataHeaderType[] = [
    {
      label: (
        <>
          {quotation.maker_name || ""}
          <br />
          {quotation.item_name || ""}
        </>
      ),
      sx: { textAlign: "left" },
    },
    { label: quotation.tire_loin_speed_mark || "" },
    { label: quotation.tire_label || "" },
    { label: quotation.quantity ? `${quotation.quantity}本` : "" },
    { label: formatCurrency(quotation.nomal_price || 0), sx: { px: 1} },
    {
      label: quotation.special_discount
        ? `${quotation.special_discount}%`
        : `${quotation.fixed_discount}%`,
    },
    { label: formatCurrency(quotation.discounted_sale_price || 0), sx: { px: 1} },
    { label: formatCurrency(quotation.wage_cost_price || 0), sx: { px: 1} },
    { label: formatCurrency(quotation.puncture_protection_price || 0), sx: { px: 1} },
    {
      label: discountAmount !== 0 ? formatCurrency(discountAmount) : '0',
    },
    {
      label: formatCurrency(quotation.confirm_total_price || 0),
      sx: { fontSize: '34px', px: 1 },
    },
    {
      label: cashback,
      sx: { fontSize: { xs: 16, md: 18, lg: 20 }, px: 1 },
    },
  ];

  // Wrap price labels with ¥ and value, same layout as TableEstDiscount
  const transformedRowData = rowData.map((item) => ({
    ...item,
    label: isPriceLabel(item.label) ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box component="span">¥</Box>
        <Box component="p" sx={{ textAlign: "center", width: "100%", my: 0 }}>
          {item.label}
        </Box>
      </Box>
    ) : (
      item.label
    ),
  }));

  return (
    <Box>
      <TableContainer
        component={Table}
        sx={{ boxShadow: "none", minWidth: "1600px" }}
      >
        <TableHead>
          <TableRowComponent data={HEADER_EST_PDF} type="header" />
        </TableHead>
        <TableBody>
          <TableRowComponent hiddenBorder data={transformedRowData} />
        </TableBody>
      </TableContainer>
      <Divider sx={{ borderBottom: "1px solid var(--gray-250)" }} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
        {quotation.midt_jan_cd ? <Barcode value={quotation.midt_jan_cd || ""} /> : <Typography style={{ fontSize: "20px", textAlign: "right" }}>&nbsp;</Typography>}
        <Typography style={{ fontSize: "20px", textAlign: "right" }}>
          備考 : {quotation.remarks || "-"}
        </Typography>
      </Box>
    </Box>
  );
}
